import { Injectable } from '@angular/core';
import { HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { HttpClientWrapper } from '~/services/api/httpClientWrapper';
import { BaseWebService } from '~/services/api/web/base/baseWebService';
import { ResponseModel } from '~/models/responseModel';
import { Observable } from 'rxjs';
import { InventoryBaleModel } from '~/models/inventoryBaleModel';
import { Dictionary, map as _map } from 'lodash';
import { InventoryLotModel } from '~/models/inventoryLotModel';
import { InventoryMobModel } from '~/models/inventoryMobModel';
import { InventorySpecieModel } from '~/models/inventorySpecieModel';
import { ServerPaginationModel } from '~/models/serverPagination/serverPaginationModel';
import { LotSummaryModel } from '~/models/lotSummaryModel';
import { ServerFilterModel } from '~/models/serverPagination/serverFilterModel';

@Injectable()
export class LotService extends BaseWebService {

    public static readonly PATH: string = '/lot';

    constructor(http: HttpClientWrapper) {
        super(http);
    }


    public searchLots(searchModel?: ServerPaginationModel<LotSummaryModel>): Observable<ResponseModel> {
        let url:string = LotService.URL + LotService.PATH + '/search';
        let body:any = searchModel;
        let options:{} = {
            headers: new HttpHeaders({'Content-Type': 'application/json'})
          };

        return this.http.post(url, body, options, (response: HttpResponse<any>) => {
            return new ServerPaginationModel<LotSummaryModel>(LotSummaryModel, response['data']);
        });
    }

    public getLotFilters(filter?: ServerFilterModel): Observable<ResponseModel> {
        let url:string = LotService.URL + LotService.PATH + '/filters';
        let body:any = filter;
        let options:{} = {
            headers: new HttpHeaders({'Content-Type': 'application/json'})
          };

        return this.http.post(url, body, options, (response: HttpResponse<any>) => {
            return response['data'];
        });
    }

    public lotSearchObfuscated(filter?: ServerFilterModel): Observable<ResponseModel> {
        let url:string = LotService.URL + LotService.PATH + '/search';
        let body:any = filter;
        let options:{} = {
            headers: new HttpHeaders({'Content-Type': 'application/json'})
          };

        return this.http.post(url, body, options, (response: HttpResponse<any>) => {
            return response['data'] as boolean;
        });
    }

    public getLotResultMeta(filter?: ServerFilterModel): Observable<ResponseModel> {
        let url:string = LotService.URL + LotService.PATH + '/resultmeta';
        let body:any = filter;
        let options:{} = {
            headers: new HttpHeaders({'Content-Type': 'application/json'})
          };

        return this.http.post(url, body, options, (response: HttpResponse<any>) => {
            return response['data'] as Dictionary<any>;
        });
    }

    public getLotById(lotId: number, entityId: number): Observable<ResponseModel> {
        let url:string = LotService.URL + '/traceability/' + entityId + '/lot/' + lotId.toString();
        let options:{} = {};

        return this.http.get(url, options, (response: HttpResponse<any>) => {
            return new InventoryLotModel(response['data']);
        });
    }

    public getLotRelatedBaleItems(lotId: number): Observable<ResponseModel> {
        let url:string = LotService.URL + LotService.PATH + '/' + lotId.toString() + '/bales';

        let options:{} = {};

        return this.http.get(url, options, (response: HttpResponse<any>) => {
            return _map(response['data'], (bale) => new InventoryBaleModel(bale));
        });
    }

    public getLotRelatedMobItems(lotId: number): Observable<ResponseModel> {
        let url:string = LotService.URL + LotService.PATH + '/' + lotId.toString() + '/mobs';

        let options:{} = {};

        return this.http.get(url, options, (response: HttpResponse<any>) => {
            return _map(response['data'], (mob) => new InventoryMobModel(mob));
        });
    }

    public getLotRelatedSpecieItems(lotId: number): Observable<ResponseModel> {
        let url:string = LotService.URL + LotService.PATH + '/' + lotId.toString() + '/species';

        let options:{} = {};

        return this.http.get(url, options, (response: HttpResponse<any>) => {
            return _map(response['data'], (specie) => new InventorySpecieModel(specie));
        });
    }


    private yieldTypeMap = {
        "03": "Estimated", 
        "04": "Schlumberger Combed Dry (1% TFM)", 
        "05": "Schlumberger Combed Dry (2% TFM)", 
        "07": "Noble Combed in Oil", 
        "08": "IWTO Scoured Yield at 16% Regain", 
        "09": "IWTO Scoured Yield at 17% Regain", 
        "10": "Japanese Clean Scoured", 
        "11": "Australian Carbonising", 
        "12": "IWTO Clean Wool Content, 17% Regain", 
        "13": "United States Custom Clean", 
        "14": "Card Sliver", 
        "15": "ASTM Clean Wool Fibre Present", 
        "16": "Noble Dry Combed", 
        "17": "Schlumberger Combed in Oil (4.6% TFM)", 
        "18": "Noble Combed Dry", 
        "19": "Theoretical Card Sliver", 
        "20": "ASTM Laboratory Scoured Yield", 
        "21": "Conditioned at 12.00%", 
        "22": "Conditioned at 13.00%", 
        "23": "Conditioned at 13.50%", 
        "24": "Conditioned at 13.64%", 
        "25": "Conditioned at 14.00%", 
        "26": "Conditioned at 14.40%", 
        "27": "Conditioned at 15.00%", 
        "28": "Conditioned at 16.00%", 
        "29": "Conditioned at 17.00%", 
        "30": "Conditioned at 18.00%", 
        "31": "Conditioned at 18.25%", 
        "32": "Japanese Clean Scoured at 17%", 
        "33": "New Zealand Process Yield", 
        "42": "IWT0 Schlumberger Oil T&N (2.0% & T.FM.)", 
        "43": "IWTO Clean Wool Content, 16% Regain" 
    }

    public getTestYieldName(yieldType: string): string {
        const yieldName = this.yieldTypeMap[yieldType];
        if (!yieldName)  {
            return "-";
        }

        return yieldName;
    }
}
